import React, { Component, ErrorInfo } from 'react';
import styles from './ErrorBoundary.module.css';
import { submitClientError } from '../../services/firebase/clientErrorService';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  incidentId: string | null;
}

function generateIncidentId(): string {
  const time = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `ZD-${time}-${rand}`.toUpperCase();
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, incidentId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, incidentId: generateIncidentId() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const incidentId = this.state.incidentId ?? generateIncidentId();

    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', incidentId, error, errorInfo);
    }

    submitClientError({
      incidentId,
      message: error.message ?? 'Unknown error',
      stack: error.stack ?? '',
      componentStack: errorInfo.componentStack ?? '',
      currentUrl: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    }).catch(() => {
      // Best-effort: never let logging failures crash the boundary itself
    });
  }

  handleReload = () => {
    window.location.href = '/main';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, incidentId: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.icon}>&#9888;</div>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.message}>
              An unexpected error occurred. You can try again or return to the main page.
            </p>
            {this.state.error && (
              <pre className={styles.errorDetail}>
                {this.state.error.message}
              </pre>
            )}
            {this.state.incidentId && (
              <p className={styles.incidentId}>
                Incident: {this.state.incidentId}
              </p>
            )}
            <div className={styles.actions}>
              <button className={styles.retryButton} onClick={this.handleRetry}>
                Try Again
              </button>
              <button className={styles.homeButton} onClick={this.handleReload}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
